import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import {
  CALL_ENDED_STATUS,
  LIVE_ASSIST_CHANNEL,
  SUPERSEDED_STATUS,
  LiveAssistFeedback,
  LiveAssistStatus,
  liveAssistPaths,
} from 'components/Alpha/LiveAssist/liveAssistContract';
import {
  openLiveAssistStream,
  postLiveAssistAsk,
  postLiveAssistCommand,
  postLiveAssistFeedback,
  serverCode,
  serverMessage,
} from 'components/Alpha/LiveAssist/liveAssistApi';
import {
  LiveAssistCardView,
  LiveAssistMessage,
  LiveAssistRating,
  LiveAssistState,
  asksToResend,
  initialLiveAssistState,
  liveAssistReducer,
} from 'components/Alpha/LiveAssist/liveAssistReducer';
import { LiveAssistMode } from 'components/Alpha/LiveAssist/liveAssistTarget';
import {
  UNAUTHORIZED_STATUS,
  liveAssistCallStanding,
  rememberLiveAssistLiveSession,
  rememberLiveAssistRefusal,
} from 'components/Alpha/LiveAssist/liveAssistCallStore';

export interface LiveAssistTiming {
  backoffMs: number[];
  healthyMs: number;
  maxFailures: number;
  callEndGraceMs: number;
  firstResponseGraceMs: number;
  idleMs: number;
  askTimeoutMs: number;
  askStallMs: number;
  askResendWaitMs: number;
}

export const DEFAULT_LIVE_ASSIST_TIMING: LiveAssistTiming = {
  backoffMs: [1000, 2000, 4000, 8000, 16000, 30000],
  healthyMs: 30000,
  maxFailures: 5,
  callEndGraceMs: 30000,
  firstResponseGraceMs: 5000,
  idleMs: 60000,
  askTimeoutMs: 90000,
  askStallMs: 30000,
  askResendWaitMs: 10000,
};

export const ASK_SEND_FAILED_TEXT =
  "Couldn't send your question to Alpha. Check your connection, then try again.";
export const ASK_NO_ANSWER_TEXT = "Alpha didn't answer. Try again.";
export const PAUSE_FAILED_TEXT = "Couldn't pause listening. Try again.";
export const RESUME_FAILED_TEXT = "Couldn't resume listening. Try again.";
export const REPLAY_FAILED_TEXT = "Couldn't start the replay. Try again.";
export const RATE_FAILED_TEXT = "Couldn't save your rating. Try again.";
export const pinFailedText = (title: string, pinned: boolean): string =>
  `Couldn't ${pinned ? 'pin' : 'unpin'} "${title}". Try again.`;
export const dismissFailedText = (title: string): string =>
  `Couldn't tell Alpha you dismissed "${title}". Try again.`;

export const FINAL_COMMAND_CODES = ['replay_not_completed', 'replay_no_transcript'];
export const STREAM_GONE_CODE = 'stream_gone';

export interface LiveAssistNotice {
  id: number;
  message: string;
  retry?: () => void;
}

export interface LiveAssistControls {
  state: LiveAssistState;
  notice?: LiveAssistNotice;
  commandBusy: boolean;
  replayStarted: boolean;
  retry: () => void;
  refreshContext: () => void;
  ask: (question: string, retryId?: string) => void;
  pause: () => void;
  resume: () => void;
  replay: () => void;
  dismiss: (card: LiveAssistCardView) => void;
  togglePin: (card: LiveAssistCardView) => void;
  rate: (card: LiveAssistCardView, rating: LiveAssistRating) => void;
  seen: () => void;
  clearNotice: () => void;
}

interface Options {
  callId?: number;
  mode?: LiveAssistMode;
  attempt?: number;
  enabled: boolean;
  timing?: LiveAssistTiming;
}

const ENDED_CONNECTIONS = ['ended', 'failed'];
const OPEN_ASK_PHASES = ['sending', 'waiting', 'streaming', 'queued'];

const randomBytes = (): Uint8Array => {
  const bytes = new Uint8Array(16);
  const source = globalThis.crypto;
  if (typeof source?.getRandomValues === 'function') return source.getRandomValues(bytes);
  return bytes.map(() => Math.floor(Math.random() * 256));
};

const newClientAskId = (): string => {
  const source = globalThis.crypto;
  if (typeof source?.randomUUID === 'function') return source.randomUUID();
  const bytes = randomBytes();
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
    16,
    20,
  )}-${hex.slice(20)}`;
};

export const useLiveAssistStream = ({
  callId,
  mode = 'live',
  attempt = 0,
  enabled,
  timing = DEFAULT_LIVE_ASSIST_TIMING,
}: Options): LiveAssistControls => {
  const [state, dispatch] = useReducer(liveAssistReducer, initialLiveAssistState);
  const [generation, setGeneration] = useState(0);
  const [notice, setNotice] = useState<LiveAssistNotice | undefined>();
  const [commandBusy, setCommandBusy] = useState(false);
  const [replayStarted, setReplayStarted] = useState(false);
  const stateRef = useRef(state);
  stateRef.current = state;
  const noticeSeq = useRef(0);
  const mounted = useRef(true);
  const timers = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const timingRef = useRef(timing);
  timingRef.current = timing;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const serverEndedRef = useRef(false);
  const [serverEnded, setServerEnded] = useState(false);
  const callOver = useCallback(
    (): boolean => modeRef.current === 'ended' || serverEndedRef.current,
    [],
  );
  const currentMode = useCallback((): LiveAssistMode => modeRef.current, []);
  const callEndedRef = useRef(false);
  const callIdRef = useRef(callId);
  callIdRef.current = callId;
  const attemptRef = useRef(attempt);
  attemptRef.current = attempt;
  const blocked = useCallback(
    (): boolean => liveAssistCallStanding(callIdRef.current, attemptRef.current).refused,
    [],
  );
  const supersededRef = useRef(false);
  const finished = useCallback((): boolean => supersededRef.current || blocked(), [blocked]);
  const endedAtRef = useRef<number | undefined>();
  const stopStreamRef = useRef<(() => void) | undefined>();

  const paths = useMemo(
    () => (typeof callId === 'number' ? liveAssistPaths(LIVE_ASSIST_CHANNEL, callId) : undefined),
    [callId],
  );

  const send = useCallback((message: LiveAssistMessage): void => {
    if (mounted.current) dispatch(message);
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
  }, []);

  const streamEnabled = enabled && (mode !== 'completed' || replayStarted);

  useEffect(() => {
    if (!streamEnabled || paths === undefined || typeof callId !== 'number') return undefined;
    if (callEndedRef.current || supersededRef.current) return undefined;
    if (blocked()) {
      send({ type: 'connection', connection: 'unavailable' });
      return undefined;
    }
    const id = callId;
    let stopped = false;
    let controller: AbortController | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let failures = 0;
    let opened = false;
    let reauthorized = false;

    const stop = (): void => {
      stopped = true;
      controller?.abort();
      clearTimeout(timer);
    };
    stopStreamRef.current = stop;

    const retryLater = (openedAt: number | undefined): void => {
      const { backoffMs, healthyMs, maxFailures } = timingRef.current;
      const healthy = openedAt !== undefined && Date.now() - openedAt >= healthyMs;
      failures = healthy ? 1 : failures + 1;
      const delay = backoffMs[Math.min(failures, backoffMs.length) - 1];
      send({ type: 'connection', connection: failures >= maxFailures ? 'failed' : 'reconnecting' });
      timer = setTimeout(() => {
        void connect();
      }, delay);
    };

    const connect = async (): Promise<void> => {
      if (stopped) return;
      if (callOver()) {
        callEndedRef.current = true;
        send({ type: 'callEnded' });
        return;
      }
      if (!opened && failures === 0) send({ type: 'connection', connection: 'connecting' });
      controller = new AbortController();
      let openedAt: number | undefined;
      const outcome = await openLiveAssistStream(
        paths.stream,
        {
          onOpen: () => {
            opened = true;
            reauthorized = false;
            openedAt = Date.now();
            if (!stopped) send({ type: 'connection', connection: 'open' });
          },
          onEvent: event => {
            if (stopped) return;
            if (event.type === 'session_started' && currentMode() === 'live' && !callOver()) {
              rememberLiveAssistLiveSession(id);
            }
            if (event.type === 'status' && event.status === CALL_ENDED_STATUS) {
              serverEndedRef.current = true;
              if (mounted.current) setServerEnded(true);
            }
            send({ type: 'event', event });
            if (event.type === 'status' && event.status === SUPERSEDED_STATUS) {
              supersededRef.current = true;
              stop();
            }
          },
        },
        controller.signal,
        timingRef.current.idleMs,
      );
      if (stopped || outcome.kind === 'aborted') return;
      if (outcome.kind === 'refused') {
        if (outcome.status === UNAUTHORIZED_STATUS && !reauthorized) {
          reauthorized = true;
          void connect();
          return;
        }
        rememberLiveAssistRefusal(id, outcome.status, attemptRef.current);
        send({ type: 'connection', connection: 'unavailable' });
        return;
      }
      if (callOver()) {
        callEndedRef.current = true;
        send({ type: 'callEnded' });
        return;
      }
      if (currentMode() === 'completed') {
        send({ type: 'connection', connection: outcome.kind === 'closed' ? 'ended' : 'failed' });
        return;
      }
      if (outcome.kind === 'busy') {
        send({ type: 'connection', connection: 'reconnecting' });
        timer = setTimeout(() => {
          void connect();
        }, outcome.retryAfterMs);
        return;
      }
      retryLater(openedAt);
    };

    void connect();
    return stop;
  }, [streamEnabled, paths, callId, generation, send, callOver, currentMode, blocked]);

  const endCall = useCallback((): void => {
    if (callEndedRef.current || finished()) return;
    callEndedRef.current = true;
    stopStreamRef.current?.();
    send({ type: 'callEnded' });
  }, [send, finished]);

  const ending = mode === 'ended' || serverEnded;
  const awaitingFirst = state.connection === 'connecting';

  useEffect(() => {
    if (!ending) {
      endedAtRef.current = undefined;
      return undefined;
    }
    if (callEndedRef.current || finished()) return undefined;
    if (endedAtRef.current === undefined) endedAtRef.current = Date.now();
    if (!awaitingFirst && stateRef.current.connection !== 'open') {
      endCall();
      return undefined;
    }
    const { callEndGraceMs, firstResponseGraceMs } = timingRef.current;
    const wait = awaitingFirst
      ? firstResponseGraceMs
      : callEndGraceMs - (Date.now() - endedAtRef.current);
    const timer = setTimeout(endCall, Math.max(0, wait));
    return () => {
      clearTimeout(timer);
    };
  }, [ending, awaitingFirst, endCall, finished]);

  const streaming = state.asks
    .filter(item => item.phase === 'streaming')
    .map(item => `${item.id}:${item.answer.length}`)
    .join('|');

  useEffect(() => {
    if (streaming === '') return undefined;
    const ids = streaming.split('|').map(item => item.slice(0, item.lastIndexOf(':')));
    const timer = setTimeout(() => {
      send({ type: 'askCutOff', ids });
    }, timingRef.current.askStallMs);
    return () => {
      clearTimeout(timer);
    };
  }, [streaming, send]);

  const retry = useCallback((): void => {
    if (callEndedRef.current || finished()) return;
    setGeneration(value => value + 1);
  }, [finished]);

  const [refreshPending, setRefreshPending] = useState(false);

  const refreshContext = useCallback((): void => {
    if (callEndedRef.current || finished()) return;
    send({ type: 'refreshContext' });
    setRefreshPending(true);
  }, [send, finished]);

  const answering = state.asks.some(item => OPEN_ASK_PHASES.includes(item.phase));

  useEffect(() => {
    if (!refreshPending || answering) return;
    setRefreshPending(false);
    if (callEndedRef.current || finished()) return;
    setGeneration(value => value + 1);
  }, [refreshPending, answering, finished]);

  const later = (ms: number, run: () => void): void => {
    if (!mounted.current) return;
    const timer = setTimeout(() => {
      timers.current = timers.current.filter(item => item !== timer);
      run();
    }, ms);
    timers.current.push(timer);
  };

  const raise = useCallback((message: string, retryAction?: () => void): void => {
    noticeSeq.current += 1;
    if (mounted.current) setNotice({ id: noticeSeq.current, message, retry: retryAction });
  }, []);

  const clearNotice = useCallback((): void => {
    setNotice(undefined);
  }, []);

  const postAsk = (question: string, replaces: string | undefined, autoResent: boolean): void => {
    if (paths === undefined) return;
    const id = newClientAskId();
    send({ type: 'askSent', id, question, replaces, autoResent });
    postLiveAssistAsk(paths.ask, question, id, stateRef.current.streamId)
      .then(({ askId }) => {
        send({ type: 'askAccepted', id, serverId: askId });
        later(timingRef.current.askTimeoutMs, () => {
          const current = stateRef.current.asks.find(item => item.id === id);
          if (current?.phase === 'waiting') {
            send({ type: 'askFailed', id, error: ASK_NO_ANSWER_TEXT, timedOut: true });
          }
        });
      })
      .catch((error: unknown) => {
        const message = serverMessage(error) ?? ASK_SEND_FAILED_TEXT;
        const gone = serverCode(error) === STREAM_GONE_CODE;
        send({ type: gone ? 'askGone' : 'askFailed', id, error: message });
      });
  };

  const postAskRef = useRef(postAsk);
  postAskRef.current = postAsk;

  const ask = (question: string, retryOf?: string): void => {
    const trimmed = question.trim();
    if (paths === undefined || trimmed === '') return;
    if (stateRef.current.streamReady) {
      postAsk(trimmed, retryOf, false);
      return;
    }
    send({
      type: 'askSent',
      id: newClientAskId(),
      question: trimmed,
      replaces: retryOf,
      deferred: true,
    });
    if (ENDED_CONNECTIONS.includes(stateRef.current.connection)) retry();
  };

  const resendKey = asksToResend(state)
    .map(item => item.id)
    .join('|');

  useEffect(() => {
    if (resendKey === '') return;
    asksToResend(stateRef.current).forEach(item => {
      postAskRef.current(item.question, item.id, item.deferred !== true);
    });
  }, [resendKey]);

  const queuedKey = state.asks
    .filter(item => item.phase === 'queued')
    .map(item => item.id)
    .join('|');
  const queueTimed = useRef<string[]>([]);

  useEffect(() => {
    if (queuedKey === '' || !mounted.current) return;
    queuedKey
      .split('|')
      .filter(id => !queueTimed.current.includes(id))
      .forEach(id => {
        queueTimed.current = [...queueTimed.current, id];
        const timer = setTimeout(() => {
          timers.current = timers.current.filter(item => item !== timer);
          send({ type: 'askQueueExpired', id, error: ASK_SEND_FAILED_TEXT });
        }, timingRef.current.askResendWaitMs);
        timers.current.push(timer);
      });
  }, [queuedKey, send]);

  const command = (
    path: string | undefined,
    failedText: string,
    onDone: (status: LiveAssistStatus | undefined) => void,
    again: () => void,
  ): void => {
    if (path === undefined || commandBusy) return;
    setCommandBusy(true);
    setNotice(undefined);
    postLiveAssistCommand(path)
      .then(onDone)
      .catch((error: unknown) => {
        const final = FINAL_COMMAND_CODES.includes(serverCode(error) ?? '');
        raise(serverMessage(error) ?? failedText, final ? undefined : again);
      })
      .finally(() => {
        if (mounted.current) setCommandBusy(false);
      });
  };

  const pause = (): void => {
    command(
      paths?.pause,
      PAUSE_FAILED_TEXT,
      status => {
        send({ type: 'event', event: { type: 'status', status: status ?? 'paused' } });
      },
      pause,
    );
  };

  const resume = (): void => {
    command(
      paths?.resume,
      RESUME_FAILED_TEXT,
      status => {
        send({ type: 'event', event: { type: 'status', status: status ?? 'listening' } });
      },
      resume,
    );
  };

  const replay = (): void => {
    command(
      paths?.replay,
      REPLAY_FAILED_TEXT,
      () => {
        if (!mounted.current) return;
        setReplayStarted(true);
        if (currentMode() !== 'completed') return;
        const again =
          callEndedRef.current || ENDED_CONNECTIONS.includes(stateRef.current.connection);
        callEndedRef.current = false;
        serverEndedRef.current = false;
        setServerEnded(false);
        if (!again) return;
        send({ type: 'replayStarted' });
        setGeneration(value => value + 1);
      },
      replay,
    );
  };

  const feedback = (
    card: LiveAssistCardView,
    action: LiveAssistFeedback,
    failedText: string,
    revert: () => void,
    again: () => void,
  ): void => {
    if (paths === undefined) return;
    postLiveAssistFeedback(paths.feedback(card.id), action).catch((error: unknown) => {
      revert();
      raise(serverMessage(error) ?? failedText, again);
    });
  };

  const currentCard = (id: string): LiveAssistCardView | undefined =>
    stateRef.current.cards.find(card => card.id === id);

  const postDismiss = (card: LiveAssistCardView): void => {
    feedback(
      card,
      'dismiss',
      dismissFailedText(card.title),
      () => undefined,
      () => {
        postDismiss(card);
      },
    );
  };

  const dismiss = (card: LiveAssistCardView): void => {
    send({ type: 'dismiss', id: card.id });
    postDismiss(card);
  };

  const togglePin = (card: LiveAssistCardView): void => {
    const pinned = !card.pinned;
    send({ type: 'pin', id: card.id, pinned });
    feedback(
      card,
      pinned ? 'pin' : 'unpin',
      pinFailedText(card.title, pinned),
      () => {
        send({ type: 'pin', id: card.id, pinned: card.pinned });
      },
      () => {
        const current = currentCard(card.id);
        if (current !== undefined && current.pinned !== pinned) togglePin(current);
      },
    );
  };

  const rate = (card: LiveAssistCardView, rating: LiveAssistRating): void => {
    if (card.rating === rating) {
      send({ type: 'rate', id: card.id, rating: undefined });
      return;
    }
    send({ type: 'rate', id: card.id, rating });
    feedback(
      card,
      rating,
      RATE_FAILED_TEXT,
      () => {
        send({ type: 'rate', id: card.id, rating: card.rating });
      },
      () => {
        const current = currentCard(card.id);
        if (current !== undefined) rate(current, rating);
      },
    );
  };

  const seen = useCallback((): void => {
    send({ type: 'seen' });
  }, [send]);

  return {
    state,
    notice,
    commandBusy,
    replayStarted,
    retry,
    refreshContext,
    ask,
    pause,
    resume,
    replay,
    dismiss,
    togglePin,
    rate,
    seen,
    clearNotice,
  };
};
